import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LogoMark, UsersIcon, TrendingIcon, CalendarIcon, ActivityIcon, BarChartIcon,
  DownloadIcon, CopyIcon, RefreshIcon, LogOutIcon, SearchIcon,
  TrashIcon, EditIcon, CheckIcon, WifiIcon, BellIcon,
} from "../ui/Icons";
import { STATUS_OPTIONS } from "../../lib/supabase";

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ size = 20 }) {
  return <span style={{ width: size, height: size, border: "2px solid var(--border)", borderTopColor: "var(--gold)", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />;
}

// ─── Bar Chart (pure CSS, no library) ────────────────────────────────────────
function SignupChart({ data, loading }) {
  if (loading || !data.length) return (
    <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {loading ? <Spin /> : <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>No data yet</span>}
    </div>
  );

  const max = Math.max(...data.map(d => d.count), 1);
  const show = data.slice(-14);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, padding: "0 4px" }}>
      {show.map(({ date, count }, i) => {
        const h = Math.max((count / max) * 100, count > 0 ? 6 : 1);
        const label = new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return (
          <div key={date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", position: "relative", cursor: "default" }}
            title={`${label}: ${count} signup${count !== 1 ? "s" : ""}`}
          >
            <div className="chart-bar" style={{
              width: "100%", maxWidth: 28,
              height: `${h}%`, minHeight: count > 0 ? 4 : 2,
              borderRadius: "3px 3px 0 0",
              background: count > 0
                ? `linear-gradient(to top, var(--gold-dark), var(--gold-light))`
                : "rgba(255,255,255,0.06)",
              animationDelay: `${i * 0.04}s`,
              transition: "opacity 0.2s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Status Select ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = { waitlist: "badge-gold", invited: "badge-blue", active: "badge-green", rejected: "badge-red" };
  return <span className={`badge ${map[status] || "badge-gold"}`}>{status || "waitlist"}</span>;
}

function StatusSelect({ id, current, onChange }) {
  return (
    <select className="select" value={current || "waitlist"} onChange={e => onChange(id, e.target.value)}
      style={{ background: "var(--bg-4)" }}>
      {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─── Realtime Toast ───────────────────────────────────────────────────────────
function RealtimeFlash({ user }) {
  if (!user) return null;
  return (
    <div className="anim-slide-down" style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 500,
      padding: "14px 18px", borderRadius: 12,
      background: "var(--bg-3)", border: "1px solid var(--gold-border)",
      boxShadow: "0 16px 48px rgba(0,0,0,0.45), 0 0 20px rgba(201,168,76,0.12)",
      display: "flex", alignItems: "center", gap: 12, maxWidth: 300,
    }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--gold-dim)", border: "1px solid var(--gold-border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gold)", flexShrink: 0 }}>
        <BellIcon size={16} />
      </div>
      <div>
        <p style={{ fontSize: "0.78rem", fontWeight: 700, fontFamily: "Space Grotesk, sans-serif", letterSpacing: "0.04em", marginBottom: 2 }}>New signup!</p>
        <p style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>{user.email}</p>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard({
  users, totalCount, dataLoading, dataError,
  stats, statsLoading, chartData, chartLoading,
  search, handleSearch,
  page, setPage, PAGE_SIZE,
  selectedIds, toggleSelect, toggleSelectAll,
  handleUpdateStatus, handleDeleteUser,
  handleBulkUpdateStatus, handleBulkDelete,
  handleExportCSV, handleCopyEmails,
  fetchUsers, fetchStats, fetchChartData,
  realtimeActive, newSignupFlash,
  logout,
}) {
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // id to confirm
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("users"); // "users" | "chart"

  const doCopy = async () => {
    await handleCopyEmails();
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const doRefresh = async () => {
    await Promise.all([fetchUsers(), fetchStats(), fetchChartData()]);
  };

  const doDelete = async (id) => {
    await handleDeleteUser(id);
    setConfirmDelete(null);
  };

  const doBulkDelete = async () => {
    await handleBulkDelete();
    setBulkConfirm(false);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const hasSelection = selectedIds.size > 0;

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const avatarColor = (email) => `hsl(${(email?.charCodeAt(0) || 0) * 17 % 360}, 38%, 38%)`;

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* Realtime flash notification */}
      <RealtimeFlash user={newSignupFlash} />

      {/* Confirm delete overlay */}
      {confirmDelete && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(5,5,8,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="anim-fade-in" style={{ background: "var(--bg-3)", border: "1px solid var(--border)", borderRadius: 16, padding: "32px 28px", maxWidth: 360, width: "100%", textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(248,113,113,0.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: "var(--red)" }}>
              <TrashIcon size={20} />
            </div>
            <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "1rem", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 8 }}>Delete User?</h3>
            <p style={{ fontSize: "0.84rem", color: "var(--text-dim)", marginBottom: 24 }}>This will permanently remove this user from the waitlist.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" onClick={() => setConfirmDelete(null)} style={{ flex: 1, padding: "10px", borderRadius: 9, fontSize: "0.85rem" }}>Cancel</button>
              <button className="btn-danger" onClick={() => doDelete(confirmDelete)} style={{ flex: 1, padding: "10px", borderRadius: 9, fontSize: "0.85rem" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {bulkConfirm && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(5,5,8,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="anim-fade-in" style={{ background: "var(--bg-3)", border: "1px solid var(--border)", borderRadius: 16, padding: "32px 28px", maxWidth: 360, width: "100%", textAlign: "center" }}>
            <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "1rem", fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 8 }}>Delete {selectedIds.size} Users?</h3>
            <p style={{ fontSize: "0.84rem", color: "var(--text-dim)", marginBottom: 24 }}>This action cannot be undone.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-ghost" onClick={() => setBulkConfirm(false)} style={{ flex: 1, padding: "10px", borderRadius: 9, fontSize: "0.85rem" }}>Cancel</button>
              <button className="btn-danger" onClick={doBulkDelete} style={{ flex: 1, padding: "10px", borderRadius: 9, fontSize: "0.85rem" }}>Delete All</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Topbar ── */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100, padding: "0 28px",
        background: "rgba(5,5,8,0.95)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderBottom: "1px solid rgba(16,183,127,0.12)",
        boxShadow: "0 1px 0 rgba(16,183,127,0.06)",
      }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
              <img src="/images/alphastack-logo-pVgok4VY (1).png" alt="AlphaStack Logo" style={{ height: 30, objectFit: "contain" }} />
            </Link>
            <div style={{ width: 1, height: 20, background: "rgba(255,255,255,0.08)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                fontFamily: "Space Grotesk, sans-serif", fontSize: "0.68rem", fontWeight: 700,
                letterSpacing: "0.12em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.35)",
              }}>
                Admin
              </span>
              <span style={{
                fontFamily: "Space Grotesk, sans-serif", fontSize: "0.68rem", fontWeight: 700,
                letterSpacing: "0.12em", textTransform: "uppercase",
                color: "var(--gold)",
              }}>
                Dashboard
              </span>
            </div>
            {/* Realtime indicator */}
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "4px 12px", borderRadius: 999,
              background: realtimeActive ? "rgba(74,222,128,0.08)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${realtimeActive ? "rgba(74,222,128,0.22)" : "rgba(255,255,255,0.07)"}`,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: realtimeActive ? "#4ADE80" : "var(--text-muted)",
                boxShadow: realtimeActive ? "0 0 8px #4ADE80" : "none",
                animation: realtimeActive ? "pulseGold 2s ease-in-out infinite" : "none",
              }} />
              <span style={{
                fontSize: "0.62rem", fontFamily: "Space Grotesk, sans-serif",
                fontWeight: 700, letterSpacing: "0.08em",
                color: realtimeActive ? "#4ADE80" : "var(--text-muted)",
              }}>
                {realtimeActive ? "LIVE" : "OFFLINE"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Link to="/" style={{
              fontSize: "0.78rem", color: "var(--text-muted)", textDecoration: "none",
              padding: "7px 14px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.07)",
              fontFamily: "Space Grotesk, sans-serif", fontWeight: 500,
              transition: "all 0.2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.color = "var(--text)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.14)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
            >← View Site</Link>
            <button className="btn-ghost" onClick={logout} style={{ padding: "7px 14px", borderRadius: 8, fontSize: "0.78rem", gap: 6, fontFamily: "Space Grotesk, sans-serif", fontWeight: 600 }}>
              <LogOutIcon size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <main style={{ maxWidth: 1300, margin: "0 auto", padding: "40px 28px" }}>
        {/* Page header */}
        <div style={{ marginBottom: 36, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <p style={{
              fontFamily: "Space Grotesk, sans-serif",
              fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em",
              textTransform: "uppercase", color: "var(--gold)", marginBottom: 8,
            }}>
              ⬡ Alpha Stack
            </p>
            <h1 style={{
              fontFamily: "Space Grotesk, sans-serif",
              fontSize: "clamp(1.7rem, 2.5vw, 2.1rem)",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              marginBottom: 8,
              color: "var(--text)",
            }}>
              Waitlist Dashboard
            </h1>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontFamily: "Space Grotesk, sans-serif" }}>
              Manage signups, track growth, and export data. Powered by Supabase Realtime.
            </p>
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 10,
            background: "rgba(16,183,127,0.06)",
            border: "1px solid rgba(16,183,127,0.15)",
          }}>
            <span style={{ fontSize: "0.68rem", fontFamily: "Space Grotesk, sans-serif", fontWeight: 600, color: "var(--text-muted)" }}>
              Last refresh:
            </span>
            <span style={{ fontSize: "0.68rem", fontFamily: "Space Grotesk, sans-serif", fontWeight: 700, color: "var(--gold)" }}>
              {new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>

        {/* ── Stats ── */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: 14,
            marginBottom: 28
          }}
        >
          {[
            { label: "Total Signups", value: stats.total, icon: <UsersIcon size={17} />, color: "var(--primary)" },
            { label: "Joined Today", value: stats.today, icon: <CalendarIcon size={17} />, color: "var(--green)" },
            { label: "This Week", value: stats.thisWeek, icon: <TrendingIcon size={17} />, color: "var(--blue)" },
            { label: "This Month", value: stats.thisMonth, icon: <ActivityIcon size={17} />, color: "var(--orange)" },
          ].map(({ label, value, icon, color }) => (
            <div
              key={label}
              className="card-flat"
              style={{
                padding: "20px 22px",
                position: "relative",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, ${color}70, transparent)`
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10
                }}
              >
                <span
                  style={{
                    fontFamily: "Space Grotesk, sans-serif",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)"
                  }}
                >
                  {label}
                </span>

                <span style={{ color, opacity: 0.75 }}>{icon}</span>
              </div>

              <div
                style={{
                  fontFamily: "Space Grotesk, sans-serif",
                  fontSize: "clamp(1.8rem, 2.5vw, 2.2rem)",
                  fontWeight: 800,
                  color,
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                  textShadow: `0 0 24px ${color}40`,
                }}
              >
                {statsLoading ? "—" : value}
              </div>
            </div>
          ))}
        </div>
        {/* ── Tab switcher ── */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
          {[["users", <UsersIcon size={14} />, "Users"], ["chart", <BarChartIcon size={14} />, "Growth Chart"]].map(([tab, icon, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 18px", background: "transparent", border: "none",
              borderBottom: `2px solid ${activeTab === tab ? "var(--gold)" : "transparent"}`,
              color: activeTab === tab ? "var(--gold)" : "var(--text-muted)",
              cursor: "pointer", fontSize: "0.8rem", fontFamily: "Space Grotesk, sans-serif",
              fontWeight: 600, letterSpacing: "0.02em",
              marginBottom: -1, transition: "color 0.2s",
            }}>{icon}{label}</button>
          ))}
        </div>

        {/* ── Chart Tab ── */}
        {activeTab === "chart" && (
          <div className="card-flat" style={{ padding: 28, marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <div>
                <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: "1rem", fontWeight: 700, marginBottom: 4 }}>Signups Over Time</h3>
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Daily signups for the last 14 days</p>
              </div>
              <button className="btn-ghost" onClick={() => fetchChartData()} style={{ padding: "7px 12px", borderRadius: 8, fontSize: "0.78rem", gap: 5 }}>
                <RefreshIcon size={13} /> Refresh
              </button>
            </div>
            <SignupChart data={chartData} loading={chartLoading} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
              <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>14 days ago</span>
              <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Today</span>
            </div>
          </div>
        )}

        {/* ── Users Tab ── */}
        {activeTab === "users" && (
          <div className="card-flat" style={{ overflow: "hidden" }}>
            {/* Toolbar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 10 }}>
              {/* Left: search + bulk actions */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", display: "flex", pointerEvents: "none" }}><SearchIcon size={14} /></span>
                  <input className="input" type="text" placeholder="Search email or name..." value={search} onChange={e => handleSearch(e.target.value)}
                    style={{ paddingLeft: 36, paddingTop: 9, paddingBottom: 9, fontSize: "0.82rem", borderRadius: 8, minWidth: 240 }} />
                </div>
                {hasSelection && (
                  <div className="anim-fade-in" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "var(--gold-dim)", border: "1px solid var(--gold-border)", borderRadius: 8 }}>
                    <span style={{ fontSize: "0.78rem", color: "var(--gold)", fontWeight: 500 }}>{selectedIds.size} selected</span>
                    <div style={{ width: 1, height: 14, background: "var(--gold-border)" }} />
                    {STATUS_OPTIONS.map(o => (
                      <button key={o.value} onClick={() => handleBulkUpdateStatus(o.value)}
                        style={{ padding: "3px 8px", borderRadius: 5, background: "transparent", border: `1px solid ${o.color}44`, color: o.color, cursor: "pointer", fontSize: "0.7rem", fontFamily: "Space Grotesk, sans-serif", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background = `${o.color}18`}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >{o.label}</button>
                    ))}
                    <button className="btn-danger" onClick={() => setBulkConfirm(true)} style={{ padding: "4px 9px", borderRadius: 5, fontSize: "0.72rem", gap: 4 }}>
                      <TrashIcon size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Right: export buttons */}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn-ghost" onClick={doRefresh} style={{ padding: "8px 13px", borderRadius: 8, fontSize: "0.79rem", gap: 6 }}>
                  <RefreshIcon size={13} /> Refresh
                </button>
                <button className="btn-ghost" onClick={doCopy} style={{ padding: "8px 13px", borderRadius: 8, fontSize: "0.79rem", gap: 6, ...(copied ? { borderColor: "rgba(74,222,128,0.3)", color: "var(--green)" } : {}) }}>
                  {copied ? <><CheckIcon size={13} /> Copied!</> : <><CopyIcon size={13} /> Copy Emails</>}
                </button>
                <button onClick={handleExportCSV} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 13px", background: "var(--gold-dim)", border: "1px solid var(--gold-border)", color: "var(--gold)", borderRadius: 8, cursor: "pointer", fontSize: "0.79rem", fontFamily: "Space Grotesk, sans-serif", transition: "background 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(201,168,76,0.2)"}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--gold-dim)"}
                ><DownloadIcon size={13} /> Export CSV</button>
              </div>
            </div>

            {/* Table */}
            {dataLoading ? (
              <div style={{ padding: "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, color: "var(--text-dim)" }}>
                <Spin size={28} />
                <p style={{ fontSize: "0.875rem" }}>Loading waitlist data from Supabase...</p>
              </div>
            ) : dataError ? (
              <div style={{ padding: "56px 24px", textAlign: "center" }}>
                <p style={{ fontSize: "0.9rem", color: "var(--red)", marginBottom: 10 }}>{dataError}</p>
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Check <code style={{ background: "var(--bg-4)", padding: "2px 6px", borderRadius: 4 }}>src/lib/supabase.js</code> credentials</p>
              </div>
            ) : users.length === 0 ? (
              <div style={{ padding: "56px 24px", textAlign: "center", color: "var(--text-dim)" }}>
                <div style={{ fontSize: "2.2rem", marginBottom: 12, opacity: 0.35 }}>📭</div>
                <p style={{ fontSize: "0.9rem" }}>{search ? `No results for "${search}"` : "No signups yet. Share your landing page!"}</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <input type="checkbox" checked={selectedIds.size === users.length && users.length > 0} onChange={toggleSelectAll} />
                      </th>
                      <th>#</th>
                      <th>Email</th>
                      <th>First Name</th>
                      <th>Status</th>
                      <th>Source</th>
                      <th>Joined At</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.id} className={selectedIds.has(u.id) ? "selected" : ""}>
                        <td><input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleSelect(u.id)} /></td>
                        <td style={{ color: "var(--text-muted)", fontFamily: "JetBrains Mono, monospace", fontSize: "0.72rem" }}>{page * PAGE_SIZE + i + 1}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: avatarColor(u.email), display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700, color: "rgba(255,255,255,0.85)", flexShrink: 0, fontFamily: "Space Grotesk, sans-serif" }}>
                              {(u.first_name || u.email || "?")[0].toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>{u.email}</span>
                          </div>
                        </td>
                        <td style={{ color: "var(--text-dim)" }}>{u.first_name || <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                        <td>
                          <StatusSelect id={u.id} current={u.status} onChange={handleUpdateStatus} />
                        </td>
                        <td style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.7rem", color: "var(--text-muted)" }}>{u.source}</td>
                        <td style={{ fontSize: "0.78rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{formatDate(u.created_at)}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                            <button className="btn-danger" onClick={() => setConfirmDelete(u.id)} style={{ padding: "5px 8px", borderRadius: 7, fontSize: "0.72rem", gap: 4 }} title="Delete user">
                              <TrashIcon size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination footer */}
            <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <p style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                Showing <strong style={{ color: "var(--text-dim)" }}>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)}</strong> of <strong style={{ color: "var(--text-dim)" }}>{totalCount}</strong> signups
              </p>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-ghost" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "5px 12px", borderRadius: 7, fontSize: "0.8rem" }}>← Prev</button>
                  <span style={{ display: "flex", alignItems: "center", padding: "0 10px", fontSize: "0.8rem", color: "var(--text-dim)" }}>Page {page + 1} / {totalPages}</span>
                  <button className="btn-ghost" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: "5px 12px", borderRadius: 7, fontSize: "0.8rem" }}>Next →</button>
                </div>
              )}
              {search && (
                <button onClick={() => handleSearch("")} style={{ fontSize: "0.72rem", color: "var(--gold)", background: "transparent", border: "none", cursor: "pointer", fontFamily: "Space Grotesk, sans-serif" }}>Clear search ×</button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
